import { PatternSimilarity } from '@/types/trading-analysis-types';

export class FuzzyMatcher {
  /**
   * Calculate Hamming distance between two binary patterns
   */
  static hammingDistance(pattern1: string, pattern2: string): number {
    if (pattern1.length !== pattern2.length) {
      return Math.max(pattern1.length, pattern2.length);
    }
    
    let distance = 0;
    for (let i = 0; i < pattern1.length; i++) {
      if (pattern1[i] !== pattern2[i]) {
        distance++;
      }
    }
    return distance;
  }

  /**
   * Calculate Levenshtein distance between two patterns
   */
  static levenshteinDistance(pattern1: string, pattern2: string): number {
    const matrix = [];
    const len1 = pattern1.length;
    const len2 = pattern2.length;

    for (let i = 0; i <= len2; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= len1; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= len2; i++) {
      for (let j = 1; j <= len1; j++) {
        if (pattern2.charAt(i - 1) === pattern1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }

    return matrix[len2][len1];
  }

  /**
   * Calculate similarity score (0-1, where 1 is identical)
   */
  static calculateSimilarity(pattern1: string, pattern2: string, method: 'hamming' | 'levenshtein' = 'hamming'): number {
    const distance = method === 'hamming' 
      ? this.hammingDistance(pattern1, pattern2)
      : this.levenshteinDistance(pattern1, pattern2);
    
    const maxLength = Math.max(pattern1.length, pattern2.length);
    return maxLength === 0 ? 1 : (maxLength - distance) / maxLength;
  }

  /**
   * Find similar patterns in a list
   */
  static findSimilarPatterns(
    targetPattern: string, 
    patterns: string[], 
    threshold: number = 0.8,
    method: 'hamming' | 'levenshtein' = 'hamming'
  ): PatternSimilarity[] {
    const similarities: PatternSimilarity[] = [];

    for (const pattern of patterns) {
      if (pattern === targetPattern) {
        similarities.push({
          pattern,
          similarity: 1,
          distance: 0,
          matchType: 'exact'
        });
        continue;
      }

      const similarity = this.calculateSimilarity(targetPattern, pattern, method);
      if (similarity >= threshold) {
        const distance = method === 'hamming' 
          ? this.hammingDistance(targetPattern, pattern)
          : this.levenshteinDistance(targetPattern, pattern);

        similarities.push({
          pattern,
          similarity,
          distance,
          matchType: 'fuzzy'
        });
      }
    }

    return similarities.sort((a, b) => b.similarity - a.similarity);
  }

  /**
   * Find patterns that match the end of input sequence with fuzzy matching
   */
  static findMatchingPatterns(
    inputSequence: string,
    patterns: string[],
    threshold: number = 0.8,
    method: 'hamming' | 'levenshtein' = 'hamming'
  ): PatternSimilarity[] {
    const matches: PatternSimilarity[] = [];

    for (const pattern of patterns) {
      // Check exact match at end of sequence
      if (inputSequence.endsWith(pattern)) {
        matches.push({
          pattern,
          similarity: 1,
          distance: 0,
          matchType: 'exact'
        });
        continue;
      }

      // Check fuzzy match with last N characters
      const minLength = Math.min(inputSequence.length, pattern.length);
      for (let len = minLength; len >= Math.min(3, minLength); len--) {
        const sequenceEnd = inputSequence.slice(-len);
        const patternStart = pattern.slice(0, len);
        
        const similarity = this.calculateSimilarity(sequenceEnd, patternStart, method);
        if (similarity >= threshold) {
          matches.push({
            pattern,
            similarity,
            distance: method === 'hamming' 
              ? this.hammingDistance(sequenceEnd, patternStart)
              : this.levenshteinDistance(sequenceEnd, patternStart),
            matchType: 'fuzzy'
          });
          break; // Take the first (longest) match for this pattern
        }
      }
    }

    return matches.sort((a, b) => b.similarity - a.similarity);
  }
}